import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../../styles/shared";
import "../../atoms/form-item";
import "../../atoms/text";
import "../../atoms/confetti-wall";
import "../../atoms/card";
import "../../atoms/dots";
import "../../atoms/intersection-flip";
import "../../atoms/section";
import "../../atoms/icon";
import "../../atoms/list-item";
import "../../atoms/title";
import "../../atoms/quote-card";
import "../../atoms/card-pagination";
import "../../molecules/landing/landing-title-header";
import "../../atoms/section-split";
import "./landing-get-started-page";
import { pushState } from "../../util/route-util";
import { ROUTES, SUPPORT_EMAIL } from "../../config";
import {
	Icon,
	ICON_EXPERIMENT,
	ICON_FACTORY,
	ICON_HAND,
	ICON_LOCATION,
	ICON_MEGAPHONE,
	ICON_UNICORN
} from "../../../icons";
import { CardPaginationItem } from "../../atoms/card-pagination";
import { repeat } from "lit/directives/repeat.js";
import { query } from "lit/decorators/query.js";
import { property } from "lit/decorators/property.js";
import { getStarted } from "../../util/landing-util";
import { styleMap } from "lit/directives/style-map.js";
import { ifDefined } from "lit/directives/if-defined.js";

interface TeamKindQuoteCardItem extends CardPaginationItem {
	id: string;
	name: string;
	icon: Icon;
	data: {
		text?: string;
		headline?: string;
		name?: string;
		label?: string;
		avatar?: string;
	};
}

const QUOTES: TeamKindQuoteCardItem[] = [
	{
		id: "marketing",
		icon: ICON_MEGAPHONE,
		name: "Marketing",
		data: {
			headline: "Brainstorm content clusters and blogpost ideas in minutes, not weeks.",
			text: "The idea visualization featured has so much potential. It was useful when we were brainstorming illustration ideas for our website. We had ideas of concepts, and the feature allowed us to have AI picturing it for us.",
			name: "Laura Ballarin",
			label: "Account Manager, ScaleCrush",
			avatar: "/assets/landing/quotes/laura.png"
		}
	},
	{
		id: "product",
		icon: ICON_FACTORY,
		name: "Product",
		data: {
			headline: "A great way for product teams to analyze and cluster thousands of feature ideas in seconds."
		}
	},
	{
		id: "manager",
		icon: ICON_LOCATION,
		name: "Manager",
		data: {
			headline: "Collaborate in real-time with AI and teammates on your OKRs and strategy.",
			text: "Since I am working alone, I really enjoyed using the program as sparring partner to my ideas and getting them more detailed",
			name: "Morten Brøgger",
			label: "Coach, brggr.com",
			avatar: "/assets/landing/quotes/morten.png"
		}
	},
	{
		id: "r&d",
		icon: ICON_EXPERIMENT,
		name: "R&D",
		data: {
			headline: "Co-create product lines with AI tools like visualization and idea generation."
		}
	},
	{
		id: "innovation",
		icon: ICON_UNICORN,
		name: "Innovation",
		data: {
			headline: "Move faster than your competition with a tools that makes your team more creative."
		}
	},
	{
		id: "hr",
		icon: ICON_HAND,
		name: "HR",
		data: {
			headline: "Make brainstorming a part of your culture while making meetings faster and more efficient."
		}
	}
];

interface LandingFeature {
	id: string;
	headline: string;
	text: string;
}

const FEATURES: LandingFeature[] = [
	{
		id: "ai-idea-generation",
		headline: "AI-Powered Productivity",
		text: "Wow your audience by running  any brainstorming session faster than ever before with time-saving AI"
	},
	{
		id: "import-and-export",
		headline: "Brainstorm with Files and Audio",
		text: "Brainstorm faster by importing ideas from all types of sources from real-time recording to powerpoints."
	},
	{
		id: "workspaces",
		headline: "A Workspace for Every Team",
		text: "Create a secure home for your team’s brainstorming sessions so you can always find ideas again"
	},
	{
		id: "share-and-embed",
		headline: "Powerful Sharing Options",
		text: "Share your brainstorming rooms with people via shareable links, posters, QR codes and more to engage people wherever they are - on their own device"
	},
	{
		id: "guests",
		headline: "Involve External Stakeholders",
		text: "Invite as many guests as you like, for free, and scale up your brainstorms"
	}
];

const customerQuotes = [
	"The ability to group, detach, and collapse is very good for organizing ideas",
	"very intuitive",
	"it was easy to grasp how things work, which is very satisfying",
	"I really enjoyed using the program as sparring partner to my ideas",
	"easy to use and very helpful"
];

function renderFeatureChips(features: string[]) {
	return html`
		<div class="feature-chips">
			${repeat(
				features,
				(feature) => html`
					<bs-chip class="feature-chip">
						<bs-text>${feature}</bs-text>
					</bs-chip>
				`
			)}
		</div>
	`;
}

@customElement("bs-landing-home-page")
class BsLandingHomePage extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					/*background: radial-gradient(
						circle,
						rgba(255, 255, 255, 0) 0%,
						rgba(11, 11, 11, 0) 63%,
						rgba(93, 255, 115, 0.05) 100%
					);*/
				}

				#cta {
					--form-item-bg: var(--primary-normal);
					--form-item-color: var(--primary-contrast-normal);
				}

				#cta-secondary-text {
					opacity: 0.8;
				}

				#hero-section {
					--section-split-gap: 100px;
				}

				#hero-section-container,
				#hero-section {
					position: relative;
				}

				#hero-dots {
					opacity: 0.7;
				}

				.feature-chips {
					display: flex;
					flex-wrap: wrap;
					gap: var(--space-m);
					margin: var(--space-l) 0 0 0;
				}

				.feature-chip {
					padding: var(--space-s) var(--space-xm);
					border-radius: 9999px;
				}

					@media (max-width: 1000px) {
							.feature-chips {
                  justify-content: center;
							}
          }
			`,
			css`
				#get-started-container {
					background: linear-gradient(150deg, var(--primary-light) -70%, var(--primary-normal) 120%);
					padding: 150px var(--space-l);
					position: relative;
					--get-started-bg: var(--background);
				}

				#get-started-card {
					margin: 0 auto;
					overflow: hidden;
					max-width: 800px;
					position: relative;
					box-shadow: 0 5px 50px -30px var(--shadow);
				}

				#get-started-page {
					min-height: unset;
				}

				#confetti-wall {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					pointer-events: none;
					opacity: 0.3;
				}
			`,
			css`
				.info-card-img {
					width: 80px;
					height: 80px;
					object-fit: cover;
					margin: 0 0 var(--space-m);
				}

				.info-card-text {
				}

				#feature-info-section {
				}

				#feature-info-cards {
					display: grid;
					grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
					gap: var(--space-l);
				}

				@media (min-width: 1000px) {
					#feature-info-cards > :nth-child(4) {
						grid-column: span 2;
					}
				}
			`,
			css`
				:host([dark]) #darkmode-content {
					color: var(--light);
				}

				:host([dark]) #darkmode-background {
					opacity: 1;
					pointer-events: all;
				}

				#darkmode-section {
					overflow: hidden;
				}

				#darkmode-content {
					position: relative;
					z-index: 1234567;
				}

				#darkmode-dots {
					--dots-color: #0e0e1c;
					--dots-fade-color: #050510;
				}

				#darkmode-background {
					opacity: 0;
					background: black;
					position: fixed;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					z-index: 123456;
					pointer-events: none;
					background: linear-gradient(150deg, #0b0b19 -70%, #050510 120%);
					transition: opacity 120ms ease-out;
				}

				#darkmode-cards {
					margin-top: 100px;
					display: grid;
					grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
					gap: var(--space-xxl);
				}

				.darkmode-card {
					background: var(--dark);
					color: var(--light);
					overflow: hidden;
				}

				.darkmode-card header {
					border: none;
				}

				.darkmode-video,
				.darkmode-image {
					object-fit: cover;
					width: 100%;
					max-width: 100%;
					border: none;
				}

				#darkmode-images {
				}

				.darkmode-image {
					position: absolute;
					width: 200px;
				}

				.darkmode-image:nth-child(1) {
					top: 180px;
					right: -90px;
					transform: rotate(10deg);
				}

				.darkmode-image:nth-child(2) {
					top: 500px;
					left: -200px;
					width: 200px;
					transform: translateZ(-3px);
				}

				.darkmode-image:nth-child(3) {
					top: 1100px;
					left: 350px;
					transform: rotate(10deg);
					width: 180px;
				}

				#darkmode-cards > * {
				}

				@media (min-width: 950px) {
					#darkmode-cards > :nth-child(2) {
						margin-top: 100px;
						transition-delay: 100ms;
					}

					#darkmode-cards > :nth-child(3) {
						margin-top: -145px;
						transition-delay: 200ms;
					}

					#darkmode-cards > :nth-child(4) {
						transition-delay: 300ms;
					}
				}

				@media (max-width: 1200px) {
					#darkmode-images {
						display: none;
					}
				}
			`,
			css`
				#customer-quotes {
					line-height: 1.5;
					font-weight: 600;
					font-size: 26px;
					margin: 0 0 100px;
					position: relative;
				}

				.customer-quote:nth-child(2n) {
					color: var(--green-dark);
				}

				.customer-quote:nth-child(4n) {
					color: var(--purple-dark);
				}

				.customer-quote:nth-child(6n) {
					color: var(--blue-dark);
				}

				#customer-quotes-label {
					text-align: center;
					text-transform: uppercase;
				}

				#customer-quotes-label a {
					color: var(--primary-dark);
				}

				.customer-quotation {
					font-size: 116px;
					position: absolute;
				}

				.customer-quotation.top {
					top: -80px;
					left: -90px;
					transition-delay: 200ms;
				}

				.customer-quotation.bottom {
					bottom: -45px;
					right: -55px;
					transition-delay: 100ms;
				}

				@media (max-width: 1000px) {
					.customer-quotation.top {
						top: -100px;
						left: 0;
					}

					.customer-quotation.bottom {
						bottom: -50px;
						right: 0;
					}
				}
			`
		];
	}

	@query("#darkmode-section") $darkmodeSection!: HTMLElement;
	@property({ type: Boolean, reflect: true }) dark = false;

	protected darkmodeIntersectionObserver!: IntersectionObserver;

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		this.setupDarkmodeIntersectionObserver();
	}

	disconnectedCallback() {
		this.darkmodeIntersectionObserver.disconnect();
		this.dispatchDarkmodeEvent(false);

		super.disconnectedCallback();
	}

	setupDarkmodeIntersectionObserver() {
		const observer = new IntersectionObserver(
			(e: IntersectionObserverEntry[]) => {
				const isIntersecting = e.find((e) => e.isIntersecting && e.intersectionRatio > 0.3) != null;
				if (isIntersecting != this.dark) {
					this.dispatchDarkmodeEvent(isIntersecting);
				}
			},
			{
				rootMargin: "0px",
				threshold: [0.7, 0.3]
			}
		);

		observer.observe(this.$darkmodeSection);
		this.darkmodeIntersectionObserver = observer;
	}

	dispatchDarkmodeEvent(dark: boolean) {
		window.dispatchEvent(new CustomEvent("darkmode", { detail: dark, bubbles: true, composed: true }));
	}

	getStarted(ref?: string) {
		getStarted(ref);
	}

	renderDarkmodeCard({ title, id, background }: { title: string; id: string; background: string }): TemplateResult {
		return html`
			<bs-intersection-flip transition>
				<bs-card class="darkmode-card" size="large" style="${styleMap({ background })}">
					<header>
						<bs-title>${title}</bs-title>
					</header>
					<article>
						<video
							class="darkmode-video"
							autoplay
							playsinline
							loop
							muted
							alt="Video of ${id}"
							src="/assets/landing/remote/${id}.mp4"
						/>
					</article>
				</bs-card>
			</bs-intersection-flip>
		`;
	}

	render() {
		return html`
			<div id="hero-section-container">
				<bs-dots id="hero-dots" fade></bs-dots>
				<bs-section-split
					id="hero-section"
					headline="Where <mark>Ideas and AI</mark> come together."
					text="Empower your team to brainstorm better and faster with Ideamap"
					illustration="/assets/landing/illustrations/hero.png"
					hero
				>
					<div slot="left">
						<bs-form-item size="huge" id="cta">
							<button slot="input" @click="${() => this.getStarted("hero")}">
								Get Started <span id="cta-secondary-text">— it's free</span>
							</button>
						</bs-form-item>
					</div>
			</div>
			<bs-section>
				<bs-landing-title-header
					label="Getting started is easy"
					headline="Built to empower every Team"
					margin
				></bs-landing-title-header>
				<bs-card-pagination
					.items="${QUOTES}"
					.renderCard="${(item: TeamKindQuoteCardItem) => {
						const { headline, text, name, label, avatar } = item.data;
						const illustration = `/assets/landing/team-kinds/${item.id}.jpg`;
						return html`
							<bs-quote-card
								id="quote-card"
								headline="${ifDefined(headline)}"
								text='"${ifDefined(text)}"'
								name="${ifDefined(name)}"
								label="${ifDefined(label)}"
								avatar="${ifDefined(avatar)}"
								illustration="${ifDefined(illustration)}"
								hoverable
								size="large"
								@click="${() => this.getStarted(`team_kind_${item.id}`)}"
							></bs-quote-card>
						`;
					}}"
				></bs-card-pagination>
			</bs-section>
			<bs-section-split
				lazy
				headline="Boost your Creativity with AI Co-Pilots"
				text="Get a second pair of eyes that never gets tired to give feedback or suggest ideas"
				illustration="/assets/landing/illustrations/copilots.png"
				flip
				transition
			></bs-section-split>
			<bs-section-split
				lazy
				headline="Brainstorm Faster with AI Actions"
				text="Save tons of time with inbuilt AI tools to analyze ideas and come with inspiration"
				illustration="/assets/landing/illustrations/ai-actions.png"
				transition
			>
				<div slot="left">
					${renderFeatureChips(["Generate Ideas", "Visualize Ideas", "Detect Topics", "Identify Duplicates"])}
				</div>
			</bs-section-split>
			<bs-section-split
				lazy
				headline="Collaborate in Real Time on an Infinite Canvas"
				text="Keep your brainstorms engaging with a playful but structured way of adding ideas and comments"
				illustration="/assets/landing/illustrations/collaboration.png"
				flip
				transition
			></bs-section-split>
			<bs-section-split
				lazy
				headline="Become a Powerful Facilitator"
				text="Collaborate with confidence and make collective decisions faster, every time"
				illustration="/assets/landing/illustrations/facilitator.png"
				transition
			>
				<div slot="left">
					${renderFeatureChips(["Vote on Ideas", "Use Framework Templates", "Conclude Brainstorms with AI", "Start Timers"])}
				</div>
			</bs-section-split>
			<bs-section id="darkmode-section">
				<div id="darkmode-content">
					<bs-landing-title-header
						label="Bring people together"
						headline="Master AI Brainstorming with your Remote Team"
						margin
					></bs-landing-title-header>
					<div id="darkmode-cards">
						${this.renderDarkmodeCard({
							title: "Easy Sharing",
							id: "sharing",
							background: "#050505"
						})}
						${this.renderDarkmodeCard({
							title: "Build on Ideas",
							id: "answer",
							background: "#18181b"
						})}
						${this.renderDarkmodeCard({
							title: "Add Reactions",
							id: "reaction",
							background: "#18181b"
						})}
						${this.renderDarkmodeCard({
							title: "Start a Timer",
							id: "timer",
							background: "#18181b"
						})}
					</div>
					<div id="darkmode-images">
						<img class="darkmode-image" src="/assets/landing/icons/idea.png" loading="lazy" />
						<img class="darkmode-image" src="/assets/landing/icons/paper-plane.png" loading="lazy" />
						<img class="darkmode-image" src="/assets/landing/icons/note.png" loading="lazy" />
					</div>
				</div>
				<div id="darkmode-background">
					<bs-dots id="darkmode-dots" fade></bs-dots>
				</div>
			</bs-section>
			<bs-section width="thin">
				<bs-intersection-flip transition>
					<bs-text size="huge" id="customer-quotes">
						${customerQuotes.map(
							(text, i) => html`<span class="customer-quote">${text}</span>${i < customerQuotes.length - 1 ? ", " : ""}`
						)}
						<bs-intersection-flip transition class="customer-quotation top">“</bs-intersection-flip>
						<bs-intersection-flip transition class="customer-quotation bottom">„</bs-intersection-flip>
					</bs-text>
					<bs-text id="customer-quotes-label" label
						>Customer quotes, <a href="mailto:${SUPPORT_EMAIL}" target="_blank">submit your quote</a></bs-text
					>
				</bs-intersection-flip>
			</bs-section>
			<bs-section id="feature-info-section" width="wide">
				<bs-landing-title-header
					label="World's #1 AI brainstorming tool"
					headline="Everything to make Brainstorms more Efficient"
					margin
				></bs-landing-title-header>
				<div id="feature-info-cards">
					${repeat(
						FEATURES,
						({ text, headline, id }) => html`
							<bs-card size="large" hoverable @click="${() => pushState(ROUTES.features(id))}">
								<article>
									<img
										class="info-card-img"
										alt="Illustration of ${id}"
										src="/assets/landing/icons/${id}.png"
										loading="lazy"
									/>
									<bs-title margin>${headline}</bs-title>
									<bs-text class="info-card-text">${text}</bs-text>
								</article>
							</bs-card>
						`
					)}
				</div>
			</bs-section>
			<div id="get-started-container">
				<bs-confetti-wall
					id="confetti-wall"
					.config="${{ maxSpeed: 0.5, maxRotationSpeed: 0.005, maxOpacitySpeed: 0, minOpacitySpeed: 0 }}"
				></bs-confetti-wall>
				<bs-card id="get-started-card">
					<bs-landing-get-started-page
						id="get-started-page"
						hide-logo
						illustration="/assets/landing/illustrations/magic-touch.png"
						headline="Theres a better way to brainstorm"
						text="Create your free account here"
					></bs-landing-get-started-page>
				</bs-card>
			</div>
		`;
	}
}
